import React, { useMemo, useRef, useState } from "react";
import { Button, Input } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ASSISTANT_ROUTES } from "../../routePaths";
import { ROUTES } from "../../../routes/patientRoutes/routePaths";
import chooseImage from "../../../assets/icons/tswira.png";
import icon1 from "../../../assets/images/logomedilink.svg";
import "../../../authPatient/pages/SignupPatient/SignupPatient.css";
import "./VerifyAssistantCode.css";

const SWAP_DURATION = 500;
const SWAP_STORAGE_KEY = "authHeroOnLeft";

export default function VerifyAssistantCode() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  const initialHeroOnLeft = (() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem(SWAP_STORAGE_KEY) : null;
    return stored === null ? true : stored === "true";
  })();

  const [heroOnLeft, setHeroOnLeft] = useState(initialHeroOnLeft);
  const heroRef = useRef(null);
  const formWrapperRef = useRef(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapStyle, setSwapStyle] = useState({ form: 0, hero: 0 });

  const codeFilled = useMemo(() => code.every((digit) => digit.trim() !== ""), [code]);

  const handleOpenAssistantInvitation = () => {
    if (isSwapping) return;

    const formEl = formWrapperRef.current;
    const heroEl = heroRef.current;

    if (formEl && heroEl) {
      const formRect = formEl.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      const formDeltaX = heroRect.left - formRect.left;
      const heroDeltaX = formRect.left - heroRect.left;
      setSwapStyle({ form: formDeltaX, hero: heroDeltaX });
    }

    setIsSwapping(true);

    const newHeroOnLeft = !heroOnLeft;

    setTimeout(() => {
      localStorage.setItem(SWAP_STORAGE_KEY, String(newHeroOnLeft));
      navigate(ASSISTANT_ROUTES.INVITATION_ACTIVATION, {
        state: { heroOnLeft: newHeroOnLeft },
      });
    }, SWAP_DURATION);
  };

  const handleChange = (value, idx) => {
    const sanitized = value.replace(/\D/g, "").slice(-1);
    setCode((prev) => {
      const next = [...prev];
      next[idx] = sanitized;
      return next;
    });

    if (sanitized && idx < inputRefs.current.length - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx) => (event) => {
    if (event.key === "Backspace" && !code[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const onFinish = () => {
    if (!codeFilled) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate(ASSISTANT_ROUTES.AVAILABILITY, { replace: true });
    }, 300);
  };

  const handleResend = () => {
    if (countdown > 0 || loading) return;
    setCode(["", "", "", "", "", ""]);
    setCountdown(30);

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  return (
    <div className="signup-container signup-doctor-auth">
      <button
        type="button"
        className="signup-back-home signup-back-home-doctor signup-hero-back-btn"
        onClick={() => navigate(ROUTES.LANDING)}
        aria-label={t("common.backToHome", "Back to home page")}
      >
        <ArrowLeftOutlined />
        <span>{t("common.backToHome", "Back to home page")}</span>
      </button>

      <div
        className={`signup-background signup-doctor ${
          heroOnLeft ? "pos-hero-left" : "pos-form-left"
        }`}
      >
        <section
          className="signup-form-wrapper"
          ref={formWrapperRef}
          style={
            isSwapping
              ? { transform: `translateX(${swapStyle.form}px)`, opacity: 0.15 }
              : undefined
          }
        >
          <div className="signup-form">
            <div className="signup-form-header">
              <h1 className="signup-form-title-main">
                {t("auth.verify")}{" "}
                <span className="signup-form-title-accent">{t("assistantPages.auth.asAssistant")}</span>
              </h1>
              <p className="signup-form-subtitle">
                {t("auth.verifyCodeSubtitle")}
              </p>
            </div>

            <div className="signup-form-content">
              <div className="signup-form-group">
                <label className="signup-form-label">{t("auth.verifyCode")}</label>

                <div className="vc-code-inputs">
                  {code.map((digit, idx) => (
                    <Input
                      key={idx}
                      ref={(el) => {
                        inputRefs.current[idx] = el;
                      }}
                      value={digit}
                      onChange={(e) => handleChange(e.target.value, idx)}
                      onKeyDown={handleKeyDown(idx)}
                      maxLength={1}
                      className="vc-code signup-form-input"
                      placeholder="0"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      disabled={loading}
                    />
                  ))}
                </div>
              </div>

              <Button
                onClick={onFinish}
                className="signup-create-account-btn"
                disabled={!codeFilled || loading}
                type="primary"
              >
                <span>{loading ? t("common.loading") : t("auth.verifyCode")}</span>
                <ArrowRightOutlined />
              </Button>

              <div className="vc-resend-section">
                <p className="vc-resend-text">{t("auth.didntReceiveCode")}</p>
                <Button
                  type="link"
                  disabled={countdown > 0 || loading}
                  className="vc-resend-btn"
                  onClick={handleResend}
                >
                  {countdown > 0
                    ? t("auth.resendInSeconds", { count: countdown })
                    : <>
                        {t("auth.resendCode", "Resend Code").split(" ").map((word, idx, arr) => 
                          word.toLowerCase() === "code" ? 
                            <span key={idx} style={{ color: "rgba(15, 66, 177, 1)" }}>{word}</span> : 
                            <span key={idx}>{word}{idx < arr.length - 1 ? " " : ""}</span>
                        )}
                      </>
                  }
                </Button>
              </div>

              <div className="vc-back-login">
                <Button
                  type="link"
                  onClick={() => navigate(ASSISTANT_ROUTES.LOGIN)}
                  className="signup-login-link"
                  disabled={loading}
                >
                  <ArrowLeftOutlined /> {t("auth.backToLogin", "Back to Login").split(" ").map((word, idx, arr) => 
                    word.toLowerCase() === "login" ? 
                      <span key={idx} style={{ color: "rgba(15, 66, 177, 1)" }}>{word}</span> : 
                      <span key={idx}>{word}{idx < arr.length - 1 ? " " : ""}</span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section
          className="signup-hero"
          ref={heroRef}
          style={{
            backgroundImage: `url(${chooseImage})`,
            ...(isSwapping
              ? { transform: `translateX(${swapStyle.hero}px)`, opacity: 0.15 }
              : {}),
          }}
        >
          <div className="signup-hero-glow" />

          <div className="signup-hero-top-icon" aria-hidden="true">
            <img src={icon1} alt={t("assistantPages.auth.decorativeIconAlt")} />
          </div>

          <div className="signup-hero-inner">
            <h1 className="signup-hero-title">{t("assistantPages.auth.heroTitle")}</h1>
            <p className="signup-hero-sub">
              {t("assistantPages.auth.heroSubtitle")}
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={handleOpenAssistantInvitation}
            >
              <span>{t("assistantPages.auth.continueAsPatient")}</span>
              <ArrowRightOutlined />
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
